import { inject } from '@angular/core';
import { RedirectFunction, Router, UrlTree } from '@angular/router';

/**
 * Resolves the configured spelling of a url whose path only differs from a configured route by
 * case, e.g. ['Confirm-Registration'] against a 'confirm-registration' route. Returns null when
 * no configured route matches case-insensitively, or when the leading segments already match a
 * configured route exactly and the url is therefore unmatched for some other reason.
 */
export const canonicalizeUnmatchedPath = (attempted: string[], configuredPaths: string[]): string[] | null => {
  const candidates = configuredPaths
    .filter(path => !!path && path !== '**')
    .map(path => path.split('/'))
    .sort((left, right) => right.length - left.length);

  for (const candidate of candidates) {
    if (candidate.length > attempted.length) {
      continue;
    }
    const resolved = candidate.map((segment, index) => segment.startsWith(':') ? attempted[index] : segment);
    const attemptedPrefix = attempted.slice(0, candidate.length).join('/');
    if (resolved.join('/') === attemptedPrefix) {
      return null;
    }
    if (resolved.join('/').toLowerCase() === attemptedPrefix.toLowerCase()) {
      return [...resolved, ...attempted.slice(candidate.length)];
    }
  }

  return null;
};

/**
 * Wildcard route handler. Angular matches paths case-sensitively, so an emailed link that comes
 * back with a differently cased path falls through to the wildcard, and a plain
 * `redirectTo: '/login'` there also discards the query string - which on an invite link is the
 * company, username and confirmation token. Send such a url to the route it was meant for, and
 * keep the query string on the way to login for everything genuinely unknown.
 */
export const redirectUnmatchedUrl: RedirectFunction = (route): UrlTree => {
  const router = inject(Router);
  const attempted = route.url.map(segment => segment.path);
  const canonical = canonicalizeUnmatchedPath(attempted, router.config.map(configured => configured.path ?? ''));

  return router.createUrlTree(canonical ?? ['login'], {
    queryParams: route.queryParams,
    fragment: route.fragment ?? undefined,
  });
};
